import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useCache from '@/hooks/useCache';
import useGetSearchParams from '@/hooks/useGetSearchParams';
import useTranslations from '@/i18n/useTranslations';
import Text from '@/shared-components/text/components/Text';

import MetaDescripProfilePages from '../MetaDescripProfilePages';

import Ratings from './ratings';
import Thoughts from './thoughts';

import './index.css';

export default function ProfileCommunity() {
  const { profileData, internal } = useContext(ProfileDataContext);
  const t = useTranslations('common.profile.pages.community');
  const [params, setParams] = useGetSearchParams({ validParams: ["v"] });
  const location = useLocation();
  const { fetchData: communityInfo } = useCache(
    `${profileData._id}_community_navigation`,
    `${config.apis.api.url}/user/userProfile/${profileData._id}`,
    {
      injectToken: true,
      includeSearchQueries: { user_type: profileData?.user_type },
      invalidateWhen: [
        "NEW_THOUGHT",
        "DELETE_THOUGHT",
        "NEW_RATING",
        `${profileData._id}_REFRESH`,
      ],
    }
  );
  const thoughtsCount = Number(communityInfo?.data?.thoughts ?? 0);
  const ratingsCount = Number(communityInfo?.data?.ratings ?? 0);
  const hasThoughts = thoughtsCount > 0;
  const hasRatings = ratingsCount > 0;

  function changeView(view) {
    const newView = { v: view };
    setView(newView);
    setParams(newView, { replace: true, preventScrollReset: true, state: location.state });
  }

  const [view, setView] = useState(
    params ? params : { v: "thoughts" }
  );

  useEffect(() => {
    if (!params?.v && !internal && !hasThoughts && hasRatings) {
      setView({ v: "ratings" });
      setParams({ v: "ratings" }, { replace: true, preventScrollReset: true, state: location.state });
    }
  }, [hasRatings, hasThoughts, internal, location.state, params?.v, setParams]);

  const shouldShowThoughtsTab = internal || hasThoughts || !communityInfo || view.v === "thoughts";
  const shouldShowRatingsTab = internal || hasRatings || !communityInfo || view.v === "ratings";

  return (
    <>
      <MetaDescripProfilePages
        fetchData={profileData}
        subPage="community"
        internal={internal}
        itemCount={thoughtsCount}
        secondaryCount={ratingsCount}
        communityView={view.v}
      />
      <div className="user-profile__community-navigation element-non-selectable">
        {
          shouldShowThoughtsTab && (
            <div className={`user-profile__community-navigation-link ${view.v === "thoughts" ? "active" : ""}`} onClick={()=>changeView("thoughts")}>
              <Text className="user-profile__community-navigation-link-text" extraSmall>
                {t('tabs.thoughts')}
              </Text>
            </div>
          )
        }

        {
          shouldShowRatingsTab && (
            <div className={`user-profile__community-navigation-link ${view.v === "ratings" ? "active" : ""}`} onClick={()=>changeView("ratings")}>
              <Text className="user-profile__community-navigation-link-text" extraSmall>
                {t('tabs.ratings')}
              </Text>
            </div>
          )
        }
      </div>

      {view.v === "thoughts" && <Thoughts profileData={profileData} internal={internal}/>}
      {view.v === "ratings" && <Ratings profileData={profileData} internal={internal}/>}
    </>
  );
}
